import { useEffect } from "react";
import { Trash2, FolderOpen, X, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useLocalSimulations, type SavedSimulation } from "@/hooks/useLocalSimulations";
import { trackEvent } from "@/lib/analytics";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoad?: (sim: SavedSimulation) => void;
}

export function SavedSimulationsModal({ open, onOpenChange, onLoad }: Props) {
  const { simulations, remove } = useLocalSimulations();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;

  const handleLoad = (sim: SavedSimulation) => {
    trackEvent("simulation_loaded");
    if (onLoad) {
      onLoad(sim);
    } else {
      // Fallback : redirige vers la home avec un flag de chargement
      try {
        window.localStorage.setItem("rentalmnp:simulation:pending-load", sim.id);
        window.location.href = "/#wizard";
      } catch {
        /* no-op */
      }
    }
    onOpenChange(false);
    toast.success("Simulation chargée");
  };

  const handleRemove = (id: string) => {
    remove(id);
    toast.success("Simulation supprimée");
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Mes simulations sauvegardées"
    >
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in-up"
        onClick={() => onOpenChange(false)}
      />
      <div className="relative w-full max-w-lg overflow-hidden rounded-t-2xl bg-background shadow-2xl animate-slide-down sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="flex items-center gap-2 text-base font-bold text-foreground"><FolderOpen className="h-4 w-4 text-primary" aria-hidden />Mes simulations</h3>
            <p className="text-xs text-muted-foreground">
              Sauvegardées sur votre appareil — jamais sur nos serveurs
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-4">
          {simulations.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <Inbox className="h-10 w-10 text-muted-foreground/60" />
              <p className="text-sm font-medium text-foreground">Aucune simulation sauvegardée</p>
              <p className="text-xs text-muted-foreground">
                Lancez une simulation et cliquez sur Sauvegarder pour la retrouver ici.
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {simulations.map((sim) => (
                <li
                  key={sim.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 transition hover:border-primary/40"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-foreground">{sim.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {new Date(sim.createdAt).toLocaleString("fr-FR", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      · {sim.input.region} · {new Intl.NumberFormat("fr-FR").format(sim.input.prixAcquisition)}€
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleLoad(sim)}
                      className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:bg-primary-dark"
                      aria-label={`Charger ${sim.name}`}
                    >
                      <FolderOpen className="h-3.5 w-3.5" />
                      Charger
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemove(sim.id)}
                      className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-danger/10 hover:text-danger"
                      aria-label={`Supprimer ${sim.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
